import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import FileUpload from "@/components/admin/FileUpload";
import { FileGrid } from "@/components/admin/PrivateFile";
import { fmtDate, titleCase, STATUS_COLORS, can, MANAGE_DOSSIERS } from "@/lib/format";
import { toast } from "sonner";
import { FileText, Lock, Plus, Trash2, Watch as WatchIcon, ShieldCheck, ExternalLink } from "lucide-react";

const DELETE_DOSSIER_ROLES = ["owner", "admin"];

function Checkpoints({ label, list, onChange, editable, byName }) {
  const [text, setText] = useState("");
  const add = () => {
    if (!text.trim()) return;
    onChange([...(list || []), { note: text.trim(), at: new Date().toISOString(), by: byName || null }]);
    setText("");
  };
  return (
    <div className="mb-5">
      <Label className="text-xs text-[var(--text-2)]">{label}</Label>
      <div className="space-y-1 my-2">
        {(list || []).map((c, i) => (
          <div key={i} className="text-xs bg-[var(--panel-2)] border border-[var(--border-lch)] rounded p-2 flex justify-between gap-3" data-testid={`checkpoint-${label}-${i}`}>
            <span className="text-[var(--text)]">{c.note}</span>
            <span className="text-[var(--muted)] shrink-0">{c.by ? `${c.by} · ` : ""}{fmtDate(c.at)}</span>
          </div>
        ))}
        {!(list || []).length && <p className="text-xs text-[var(--muted)]">No entries yet.</p>}
      </div>
      {editable && (
        <div className="flex gap-2">
          <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Add checkpoint…" className="bg-[var(--panel)]"
            data-testid={`checkpoint-input-${label}`}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} />
          <Button size="sm" variant="outline" onClick={add} data-testid={`checkpoint-add-${label}`}><Plus size={14} /></Button>
        </div>
      )}
    </div>
  );
}

const RESULT_COLORS = {
  passed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  failed: "bg-red-100 text-red-800 border-red-200",
  inconclusive: "bg-amber-100 text-amber-800 border-amber-200",
};

export default function AdminDossiers() {
  const { user } = useAdminAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const editable = can(user?.role, MANAGE_DOSSIERS);
  const canDelete = DELETE_DOSSIER_ROLES.includes(user?.role);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [hist, setHist] = useState({ type: "re_authentication", authenticator: "", result: "passed", notes: "" });

  const load = useCallback(async () => {
    const r = await api.get("/admin/dossiers");
    setItems(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openDossier = useCallback(async (id) => {
    try {
      const r = await api.get(`/admin/dossiers/${id}`);
      setSelected(r.data);
      setForm(r.data);
    } catch {
      toast.error("Dossier not found");
    }
  }, []);

  // Deep-link: /admin/dossiers?open=<dossier_id> (used by "View Dossier" from Watches)
  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId && (!selected || selected.id !== openId)) {
      openDossier(openId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const closeDialog = () => {
    setSelected(null);
    setForm(null);
    if (searchParams.get("open")) {
      searchParams.delete("open");
      setSearchParams(searchParams, { replace: true });
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    try {
      await api.put(`/admin/dossiers/${form.id}`, form);
      toast.success("Dossier saved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };

  const addHistory = async () => {
    if (!hist.authenticator.trim() && !hist.notes.trim()) {
      toast.error("Add an authenticator or notes for the record");
      return;
    }
    try {
      const r = await api.post(`/admin/dossiers/${form.id}/history`, hist);
      setForm(r.data);
      setHist({ type: "re_authentication", authenticator: "", result: "passed", notes: "" });
      toast.success("History entry recorded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record entry");
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/dossiers/${toDelete.id}`);
      toast.success("Dossier deleted");
      setToDelete(null);
      closeDialog();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <div data-testid="admin-dossiers">
      <div className="flex items-center gap-2 mb-1">
        <FileText className="text-[#c9a86a]" size={22} />
        <h1 className="text-2xl font-semibold">Digital Dossiers</h1>
        <Badge variant="outline" className="bg-[var(--panel)] text-[#c9a86a] border-[var(--border-2)] text-[10px] ml-1">
          <Lock size={10} className="mr-1" />PRIVATE
        </Badge>
      </div>
      <p className="text-sm text-[var(--text-2)] mb-4">
        Internal source-of-truth records. Generate one from any watch (Watches → Generate Dossier),
        then track media, authentication documents, checkpoints, and re-authentication history.
      </p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((d) => (
          <Card key={d.id} className="p-4 border-[var(--border-lch)] cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => openDossier(d.id)} data-testid={`dossier-${d.id}`}>
            <div className="flex items-center justify-between">
              <div className="text-[11px] text-[var(--muted)] tracking-wide">{d.dossier_id}</div>
              {d.watch_id && <Badge variant="outline" className="text-[10px] bg-[var(--panel-2)] text-[#9a7b3f] border-[#e3d6bb]"><WatchIcon size={10} className="mr-1" />Linked</Badge>}
            </div>
            <div className="font-medium mt-1">{d.brand} {d.model}</div>
            <div className="text-xs text-[var(--text-2)]">Ref {d.reference_number || "—"} · {d.year || "—"}</div>
            <div className="flex items-center gap-3 text-[11px] text-[var(--muted)] mt-2">
              <span>Updated {fmtDate(d.updated_at)}</span>
              {(d.re_authentication_history?.length > 0) && (
                <span className="flex items-center gap-1"><ShieldCheck size={11} />{d.re_authentication_history.length} auth log</span>
              )}
            </div>
          </Card>
        ))}
        {!items.length && (
          <Card className="p-8 text-center text-[var(--muted)] border-[var(--border-lch)] col-span-full" data-testid="dossiers-empty">
            No dossiers yet. Open <span className="text-[var(--text-2)] font-medium">Watches</span> and choose
            <span className="text-[var(--text-2)] font-medium"> Generate Dossier</span> on any piece.
          </Card>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {form && (<>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 flex-wrap">
                <span>{form.dossier_id} — {form.brand} {form.model}</span>
                <Badge variant="outline" className="bg-[var(--panel)] text-[#c9a86a] border-[var(--border-2)] text-[10px]">
                  <Lock size={10} className="mr-1" />PRIVATE
                </Badge>
              </DialogTitle>
            </DialogHeader>
            <Tabs defaultValue="overview">
              <TabsList className="flex-wrap h-auto">
                <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
                <TabsTrigger value="media" data-testid="tab-media">Media</TabsTrigger>
                <TabsTrigger value="docs" data-testid="tab-docs">Documents</TabsTrigger>
                <TabsTrigger value="checkpoints" data-testid="tab-checkpoints">Checkpoints</TabsTrigger>
                <TabsTrigger value="authentication" data-testid="tab-authentication">Authentication</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-3 pt-3">
                {form.watch_id ? (
                  <div className="flex items-center justify-between bg-[var(--panel-2)] border border-[#e3d6bb] rounded-md p-3" data-testid="linked-watch-banner">
                    <div className="flex items-center gap-2 text-sm text-[#6b5a33]">
                      <WatchIcon size={16} className="text-[#9a7b3f]" />
                      Linked to inventory item · {form.brand} {form.model}
                    </div>
                    <Button size="sm" variant="outline" data-testid="open-linked-watch"
                      onClick={() => navigate(`/admin/watches/${form.watch_id}`)}
                      className="border-[#c9a86a] text-[#9a7b3f] hover:bg-[#ece2cd]">
                      <ExternalLink size={14} className="mr-1" /> Open Watch
                    </Button>
                  </div>
                ) : (
                  <div className="text-xs text-[var(--muted)] bg-[var(--panel-2)] border border-[var(--border-lch)] rounded p-2">Standalone dossier (not linked to an inventory item).</div>
                )}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5"><Label className="text-xs">Reference</Label><Input value={form.reference_number ?? ""} disabled={!editable} onChange={(e) => set("reference_number", e.target.value)} className="bg-[var(--panel)]" /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Year</Label><Input value={form.year ?? ""} disabled={!editable} onChange={(e) => set("year", e.target.value)} className="bg-[var(--panel)]" /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Weight</Label><Input value={form.weight ?? ""} disabled={!editable} onChange={(e) => set("weight", e.target.value)} className="bg-[var(--panel)]" data-testid="dossier-weight" /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Measurements</Label><Input value={form.measurements ?? ""} disabled={!editable} onChange={(e) => set("measurements", e.target.value)} className="bg-[var(--panel)]" /></div>
                </div>
                <div className="space-y-1.5"><Label className="text-xs">Serial (Private)</Label><Input value={form.serial_private ?? ""} disabled={!editable} onChange={(e) => set("serial_private", e.target.value)} className="bg-[var(--panel)]" data-testid="dossier-serial" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Condition Report</Label><Textarea rows={2} value={form.condition_report ?? ""} disabled={!editable} onChange={(e) => set("condition_report", e.target.value)} className="bg-[var(--panel)]" data-testid="dossier-condition" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Service History</Label><Textarea rows={2} value={form.service_history ?? ""} disabled={!editable} onChange={(e) => set("service_history", e.target.value)} className="bg-[var(--panel)]" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Ownership / Source Notes</Label><Textarea rows={2} value={form.ownership_notes ?? ""} disabled={!editable} onChange={(e) => set("ownership_notes", e.target.value)} className="bg-[var(--panel)]" /></div>
              </TabsContent>

              <TabsContent value="media" className="space-y-4 pt-3">
                {[["private_photos", "Private Photos"], ["macro_photos", "Macro Photos"], ["three_sixty_photos", "3D / 360 Photos"]].map(([key, lbl]) => (
                  <div key={key}>
                    <Label className="text-xs text-[var(--text-2)]">{lbl}</Label>
                    <div className="my-2"><FileGrid files={form[key]} onRemove={editable ? (f) => set(key, (form[key] || []).filter((x) => x.id !== f.id)) : undefined} /></div>
                    {editable && <FileUpload visibility="private" category={`dossier_${key}`} label={`Add ${lbl}`} onUploaded={(fs) => set(key, [...(form[key] || []), ...fs])} />}
                  </div>
                ))}
              </TabsContent>

              <TabsContent value="docs" className="space-y-3 pt-3">
                <Label className="text-xs text-[var(--text-2)]">Authentication Documents</Label>
                <div className="my-2"><FileGrid files={form.authentication_documents} onRemove={editable ? (f) => set("authentication_documents", (form.authentication_documents || []).filter((x) => x.id !== f.id)) : undefined} /></div>
                {editable && <FileUpload visibility="private" accept="image/*,application/pdf" category="dossier_auth" label="Upload Auth Docs" onUploaded={(fs) => set("authentication_documents", [...(form.authentication_documents || []), ...fs])} />}
              </TabsContent>

              <TabsContent value="checkpoints" className="pt-3">
                <Checkpoints label="Inspection Checkpoints" list={form.inspection_checkpoints} onChange={(v) => set("inspection_checkpoints", v)} editable={editable} byName={user?.name} />
                <Checkpoints label="Shipping Checkpoints" list={form.shipping_checkpoints} onChange={(v) => set("shipping_checkpoints", v)} editable={editable} byName={user?.name} />
                <Checkpoints label="Return Inspection Checkpoints" list={form.return_inspection_checkpoints} onChange={(v) => set("return_inspection_checkpoints", v)} editable={editable} byName={user?.name} />
              </TabsContent>

              <TabsContent value="authentication" className="pt-3 space-y-4">
                <div className="flex items-center gap-2">
                  <ShieldCheck size={16} className="text-[#c9a86a]" />
                  <Label className="text-sm font-medium text-[var(--text)]">Re-Authentication & Return Inspection History</Label>
                </div>
                <p className="text-xs text-[var(--muted)] -mt-2">Append-only record. Entries are preserved and cannot be edited or removed.</p>

                <div className="space-y-2" data-testid="auth-history-list">
                  {(form.re_authentication_history || []).slice().reverse().map((h) => (
                    <div key={h.id} className="border border-[var(--border-lch)] rounded-md p-3 bg-[var(--panel-2)]" data-testid={`auth-history-${h.id}`}>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px] bg-[var(--panel)]">{titleCase(h.type)}</Badge>
                          {h.result && <Badge variant="outline" className={`text-[10px] ${RESULT_COLORS[h.result] || STATUS_COLORS[h.result] || ""}`}>{titleCase(h.result)}</Badge>}
                        </div>
                        <span className="text-[11px] text-[var(--muted)]">{fmtDate(h.date || h.at)}</span>
                      </div>
                      {h.authenticator && <div className="text-sm text-[var(--text)] mt-1.5">Authenticator: {h.authenticator}</div>}
                      {h.notes && <div className="text-xs text-[var(--text-2)] mt-1">{h.notes}</div>}
                      <div className="text-[10px] text-[var(--muted)] mt-1.5">Recorded by {h.by_name || "—"}</div>
                    </div>
                  ))}
                  {!(form.re_authentication_history || []).length && <p className="text-xs text-[var(--muted)]">No authentication history recorded yet.</p>}
                </div>

                {editable && (
                  <div className="border border-dashed border-[var(--border-2)] rounded-md p-3 space-y-3" data-testid="auth-history-form">
                    <Label className="text-xs text-[var(--text-2)]">Add History Entry</Label>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-[11px] text-[var(--text-2)]">Type</Label>
                        <Select value={hist.type} onValueChange={(v) => setHist((h) => ({ ...h, type: v }))}>
                          <SelectTrigger className="bg-[var(--panel)]" data-testid="auth-type-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="re_authentication">Re-Authentication</SelectItem>
                            <SelectItem value="return_inspection">Return Inspection</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-[11px] text-[var(--text-2)]">Result</Label>
                        <Select value={hist.result} onValueChange={(v) => setHist((h) => ({ ...h, result: v }))}>
                          <SelectTrigger className="bg-[var(--panel)]" data-testid="auth-result-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="passed">Passed</SelectItem>
                            <SelectItem value="failed">Failed</SelectItem>
                            <SelectItem value="inconclusive">Inconclusive</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-[11px] text-[var(--text-2)]">Authenticator</Label>
                      <Input value={hist.authenticator} onChange={(e) => setHist((h) => ({ ...h, authenticator: e.target.value }))} placeholder="e.g. Watchfinder Authentication" className="bg-[var(--panel)]" data-testid="auth-authenticator-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-[11px] text-[var(--text-2)]">Notes</Label>
                      <Textarea rows={2} value={hist.notes} onChange={(e) => setHist((h) => ({ ...h, notes: e.target.value }))} placeholder="Findings, movement check, condition on return…" className="bg-[var(--panel)]" data-testid="auth-notes-input" />
                    </div>
                    <div className="flex justify-end">
                      <Button size="sm" onClick={addHistory} data-testid="auth-history-add" className="bg-[var(--panel-2)] text-[#c9a86a] hover:bg-[var(--border-2)]">
                        <Plus size={14} className="mr-1" /> Record Entry
                      </Button>
                    </div>
                  </div>
                )}
              </TabsContent>
            </Tabs>

            <div className="flex justify-between items-center mt-4">
              {canDelete ? (
                <Button variant="outline" onClick={() => setToDelete(form)} data-testid="dossier-delete"
                  className="text-red-600 border-red-200 hover:bg-red-50">
                  <Trash2 size={14} className="mr-1" /> Delete Dossier
                </Button>
              ) : <span />}
              {editable && <Button onClick={save} data-testid="dossier-save" className="bg-[var(--panel-2)] text-[#c9a86a] hover:bg-[var(--border-2)]">Save Dossier</Button>}
            </div>
          </>)}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this dossier?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete?.dossier_id} — {toDelete?.brand} {toDelete?.model} will be permanently removed,
              including its authentication history. The linked watch will be unlinked. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="dossier-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
